import { create } from 'zustand';
import { authenticatedGet, authenticatedDelete, handleApiResponse } from '../../../utils/apiClient';

/**
 * Chat session interface
 */
export interface ChatSession {
  id: string;
  title: string;
  created_at?: string;
  updated_at?: string;
  titleGenerated?: boolean;
  titleUpdating?: boolean;
  oldTitle?: string;
  [key: string]: any;
}

/**
 * Chat session store state interface
 */
export interface ChatSessionState {
  // State
  sessions: ChatSession[];
  isLoading: boolean;
  isLoadingMore: boolean; // Loading more sessions (pagination)
  error: string | null;
  deletingSessionId: string | null;
  hasMore: boolean; // Whether there are more sessions to load
  totalCount: number; // Total number of sessions
  currentPage: number; // Current page number (for offset calculation)
  pageSize: number; // Number of sessions per page

  // Actions
  fetchSessions: (reset?: boolean) => Promise<boolean>;
  loadMoreSessions: () => Promise<boolean>;
  refreshSessions: () => Promise<boolean>;
  clearError: () => void;
  resetStore: () => void;
  getSessionById: (sessionId: string) => ChatSession | null;
  addNewSession: (session: ChatSession) => void;
  updateSessionTitle: (sessionId: string, newTitle: string) => void;
  checkTitleUpdate: (sessionId: string) => Promise<boolean>;
  deleteSession: (sessionId: string) => Promise<boolean>;
}

/**
 * Chat Session Store
 *
 * Manages chat session data for the authenticated user including:
 * - Fetching sessions from the backend API
 * - Loading states and error handling
 * - Session list management
 */
const useChatSessionStore = create<ChatSessionState>((set, get) => ({
  // State
  sessions: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  deletingSessionId: null,
  hasMore: false,
  totalCount: 0,
  currentPage: 0,
  pageSize: 20,

  // Actions

  /**
   * Fetch chat sessions for the current user
   * @param reset - If true, clears existing sessions and loads first page; if false, loads next page
   * @returns - True if fetch was successful
   */
  fetchSessions: async (reset = true) => {
    const { pageSize, currentPage } = get();
    const page = reset ? 0 : currentPage;
    const offset = page * pageSize;

    // Set appropriate loading state
    if (reset) {
      set({ isLoading: true, error: null });
    } else {
      set({ isLoadingMore: true, error: null });
    }

    try {
      const response = await authenticatedGet(`/api/chat/sessions?limit=${pageSize}&offset=${offset}`);
      const data = await handleApiResponse(Promise.resolve(response));

      if (data && Array.isArray(data.sessions)) {
        const newSessions = reset ? data.sessions : [...get().sessions, ...data.sessions];

        set({
          sessions: newSessions,
          isLoading: false,
          isLoadingMore: false,
          error: null,
          hasMore: data.has_more || false,
          totalCount: data.total_count || data.sessions.length,
          currentPage: page
        });
        return true;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      if (error.message === 'Invalid or expired token' || error.message === 'Authentication failed. Please log in again.') {
        const authStoreModule = await import('../../../stores/authStore');
        const { logout } = authStoreModule.default.getState();
        logout();
        set({
          isLoading: false,
          isLoadingMore: false,
          error: 'authentication_failed'
        });
        return false;
      }

      const errorMessage = error.message || 'Failed to fetch chat sessions';
      set({
        isLoading: false,
        isLoadingMore: false,
        error: errorMessage
      });
      console.error('Error fetching sessions:', error);
      return false;
    }
  },

  /**
   * Load more sessions (pagination)
   * @returns - True if load was successful
   */
  loadMoreSessions: async () => {
    const { hasMore, isLoadingMore, isLoading } = get();

    // Don't load if already loading or no more sessions
    if (isLoadingMore || isLoading || !hasMore) {
      return false;
    }

    // Increment page and fetch next batch
    const nextPage = get().currentPage + 1;
    set({ currentPage: nextPage });
    return get().fetchSessions(false);
  },

  /**
   * Refresh sessions (alias for fetchSessions for consistency)
   * @returns - True if refresh was successful
   */
  refreshSessions: async () => {
    return get().fetchSessions(true);
  },

  /**
   * Clear any existing error
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset the store to initial state
   */
  resetStore: () => {
    set({
      sessions: [],
      isLoading: false,
      isLoadingMore: false,
      error: null,
      hasMore: false,
      totalCount: 0,
      currentPage: 0
    });
  },

  /**
   * Get session by ID
   * @param sessionId - The session ID to find
   * @returns - The session object or null if not found
   */
  getSessionById: (sessionId) => {
    const { sessions } = get();
    return sessions.find(session => session.id === sessionId) || null;
  },

  /**
   * Add a new session to the top of the list
   * Used when a new session is created to avoid full refresh
   * @param session - The session object to add
   */
  addNewSession: (session) => {
    const { sessions, totalCount } = get();
    const exists = sessions.some(s => s.id === session.id);
    if (!exists) {
      set({
        sessions: [session, ...sessions],
        totalCount: totalCount + 1
      });
    }
  },

  /**
   * Update a session's title in the local store
   * Used when AI generates a new title
   * @param sessionId - The session ID to update
   * @param newTitle - The new title
   */
  updateSessionTitle: (sessionId, newTitle) => {
    const { sessions } = get();
    const updatedSessions = sessions.map(session =>
      session.id === sessionId
        ? {
            ...session,
            title: newTitle,
            titleGenerated: true,
            titleUpdating: true,
            oldTitle: session.title
          }
        : session
    );
    set({ sessions: updatedSessions });

    setTimeout(() => {
      const { sessions } = get();
      const clearedSessions = sessions.map(session =>
        session.id === sessionId
          ? { ...session, titleUpdating: false, oldTitle: undefined }
          : session
      );
      set({ sessions: clearedSessions });
    }, 2000);
  },

  /**
   * Check if a session's title has been updated by AI
   * Fetches the latest session data from the backend and updates if title changed
   * @param sessionId - The session ID to check
   * @returns - True if title was updated
   */
  checkTitleUpdate: async (sessionId) => {
    try {
      const response = await authenticatedGet('/api/chat/sessions');
      const data = await handleApiResponse(Promise.resolve(response));

      if (data && Array.isArray(data.sessions)) {
        const updatedSession = data.sessions.find((s: ChatSession) => s.id === sessionId);

        if (updatedSession) {
          const { sessions, updateSessionTitle } = get();
          const currentSession = sessions.find(s => s.id === sessionId);

          if (currentSession && currentSession.title !== updatedSession.title) {
            updateSessionTitle(sessionId, updatedSession.title);
            return true;
          }
        }
      }
      return false;
    } catch (error: any) {
      console.error('[checkTitleUpdate] Error checking title update:', error);
      return false;
    }
  },

  /**
   * Delete a chat session
   * @param sessionId - The session ID to delete
   * @returns - True if deletion was successful
   */
  deleteSession: async (sessionId) => {
    set({ deletingSessionId: sessionId, error: null });

    try {
      const response = await authenticatedDelete(`/api/chat/sessions/${sessionId}`);
      const data = await handleApiResponse(Promise.resolve(response));

      if (response.ok) {
        await new Promise(resolve => setTimeout(resolve, 400));

        const { sessions, totalCount } = get();
        const updatedSessions = sessions.filter(session => session.id !== sessionId);

        set({
          sessions: updatedSessions,
          deletingSessionId: null,
          error: null,
          totalCount: Math.max(0, totalCount - 1)
        });
        return true;
      } else {
        throw new Error(data.error || 'Failed to delete session');
      }
    } catch (error: any) {
      if (error.message === 'Invalid or expired token' || error.message === 'Authentication failed. Please log in again.') {
        const authStoreModule = await import('../../../stores/authStore');
        const { logout } = authStoreModule.default.getState();
        logout();
        set({
          deletingSessionId: null,
          error: 'authentication_failed'
        });
        return false;
      }

      const errorMessage = error.message || 'Failed to delete session';
      set({
        deletingSessionId: null,
        error: errorMessage
      });
      console.error('Error deleting session:', error);
      return false;
    }
  }
}));

export default useChatSessionStore;
